import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Palette,
  Upload,
  X,
  Eye,
  Globe,
  Type,
  Image as ImageIcon,
  Phone,
} from "lucide-react";

interface StoreData {
  name: string;
  description: string;
  logo: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  items: any[];
  isPublished: boolean;
  storeUrl: string;
  contactInfo?: {
    email: string;
    phone: string;
    location: string;
    businessHours: {
      weekdays: string;
      saturday: string;
      sunday: string;
    };
  };
}

interface WebsiteCustomizerProps {
  storeData: StoreData;
  onStoreUpdate: (updates: Partial<StoreData>) => void;
}

const WebsiteCustomizer = ({
  storeData,
  onStoreUpdate,
}: WebsiteCustomizerProps) => {
  const [previewMode, setPreviewMode] = useState(false);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const logoUrl = URL.createObjectURL(file);
      onStoreUpdate({ logo: logoUrl });
    }
  };

  const removeLogo = () => {
    onStoreUpdate({ logo: "" });
  };

  const colorPresets = [
    {
      name: "Traditional",
      primary: "#8B4513",
      secondary: "#DAA520",
      accent: "#FFD700",
    },
    {
      name: "Royal",
      primary: "#2C1810",
      secondary: "#8B4513",
      accent: "#DAA520",
    },
    {
      name: "Vibrant",
      primary: "#FF6B35",
      secondary: "#F7931E",
      accent: "#FFD700",
    },
    {
      name: "Earth",
      primary: "#8B4513",
      secondary: "#A0522D",
      accent: "#DEB887",
    },
    {
      name: "Modern",
      primary: "#2C3E50",
      secondary: "#34495E",
      accent: "#E74C3C",
    },
  ];

  const applyColorPreset = (preset: (typeof colorPresets)[0]) => {
    onStoreUpdate({
      primaryColor: preset.primary,
      secondaryColor: preset.secondary,
      accentColor: preset.accent,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold">Website Customizer</h3>
          <p className="text-muted-foreground">
            Customize your store's appearance and branding
          </p>
        </div>
        <Button variant="outline" onClick={() => setPreviewMode(!previewMode)}>
          <Eye className="h-4 w-4 mr-2" />
          {previewMode ? "Exit Preview" : "Live Preview"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customization Panel */}
        <div className="space-y-6">
          {/* Store Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Store Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="store-name">Store Name</Label>
                <Input
                  id="store-name"
                  value={storeData.name}
                  onChange={(e) => onStoreUpdate({ name: e.target.value })}
                  placeholder="My Kente Store"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="store-description">Store Description</Label>
                <Textarea
                  id="store-description"
                  value={storeData.description}
                  onChange={(e) =>
                    onStoreUpdate({ description: e.target.value })
                  }
                  placeholder="Describe your store..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Logo Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Store Logo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {storeData.logo ? (
                <div className="space-y-4">
                  <div className="relative inline-block">
                    <img
                      src={storeData.logo}
                      alt="Store Logo"
                      className="h-20 w-20 object-contain border rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 h-6 w-6 p-0"
                      onClick={removeLogo}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Logo uploaded successfully
                  </p>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                    aria-label="Upload store logo"
                  />
                  <label
                    htmlFor="logo-upload"
                    className="flex flex-col items-center justify-center cursor-pointer"
                  >
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">
                      Click to upload your store logo
                    </span>
                    <span className="text-xs text-gray-500 mt-1">
                      PNG, JPG up to 2MB
                    </span>
                  </label>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Color Scheme */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Color Scheme
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Color Presets */}
              <div>
                <Label className="text-sm font-medium mb-3 block">
                  Quick Color Presets
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  {colorPresets.map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => applyColorPreset(preset)}
                      className="p-3 border rounded-lg hover:border-primary transition-colors text-left"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex gap-1">
                          <div
                            className="w-4 h-4 rounded-full border"
                            style={{ backgroundColor: preset.primary }}
                          />
                          <div
                            className="w-4 h-4 rounded-full border"
                            style={{ backgroundColor: preset.secondary }}
                          />
                          <div
                            className="w-4 h-4 rounded-full border"
                            style={{ backgroundColor: preset.accent }}
                          />
                        </div>
                        <span className="text-sm font-medium">
                          {preset.name}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Colors */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Custom Colors</Label>

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="primary-color" className="text-xs">
                      Primary
                    </Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        id="primary-color"
                        value={storeData.primaryColor}
                        onChange={(e) =>
                          onStoreUpdate({ primaryColor: e.target.value })
                        }
                        className="w-8 h-8 rounded border"
                        aria-label="Primary color picker"
                      />
                      <Input
                        value={storeData.primaryColor}
                        onChange={(e) =>
                          onStoreUpdate({ primaryColor: e.target.value })
                        }
                        className="text-xs"
                        placeholder="#8B4513"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="secondary-color" className="text-xs">
                      Secondary
                    </Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        id="secondary-color"
                        value={storeData.secondaryColor}
                        onChange={(e) =>
                          onStoreUpdate({ secondaryColor: e.target.value })
                        }
                        className="w-8 h-8 rounded border"
                        aria-label="Secondary color picker"
                      />
                      <Input
                        value={storeData.secondaryColor}
                        onChange={(e) =>
                          onStoreUpdate({ secondaryColor: e.target.value })
                        }
                        className="text-xs"
                        placeholder="#DAA520"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="accent-color" className="text-xs">
                      Accent
                    </Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        id="accent-color"
                        value={storeData.accentColor}
                        onChange={(e) =>
                          onStoreUpdate({ accentColor: e.target.value })
                        }
                        className="w-8 h-8 rounded border"
                        aria-label="Accent color picker"
                      />
                      <Input
                        value={storeData.accentColor}
                        onChange={(e) =>
                          onStoreUpdate({ accentColor: e.target.value })
                        }
                        className="text-xs"
                        placeholder="#FFD700"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contact-email">Email Address</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={storeData.contactInfo?.email || "info@kentestore.com"}
                  onChange={(e) =>
                    onStoreUpdate({
                      contactInfo: {
                        email: e.target.value,
                        phone:
                          storeData.contactInfo?.phone || "+233 24 123 4567",
                        location:
                          storeData.contactInfo?.location || "Accra, Ghana",
                        businessHours: storeData.contactInfo?.businessHours || {
                          weekdays: "9:00 AM - 6:00 PM",
                          saturday: "10:00 AM - 4:00 PM",
                          sunday: "Closed",
                        },
                      },
                    })
                  }
                  placeholder="info@yourstore.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-phone">Phone Number</Label>
                <Input
                  id="contact-phone"
                  value={storeData.contactInfo?.phone || "+233 24 123 4567"}
                  onChange={(e) =>
                    onStoreUpdate({
                      contactInfo: {
                        email:
                          storeData.contactInfo?.email || "info@kentestore.com",
                        phone: e.target.value,
                        location:
                          storeData.contactInfo?.location || "Accra, Ghana",
                        businessHours: storeData.contactInfo?.businessHours || {
                          weekdays: "9:00 AM - 6:00 PM",
                          saturday: "10:00 AM - 4:00 PM",
                          sunday: "Closed",
                        },
                      },
                    })
                  }
                  placeholder="+233 24 123 4567"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-location">Location</Label>
                <Input
                  id="contact-location"
                  value={storeData.contactInfo?.location || "Accra, Ghana"}
                  onChange={(e) =>
                    onStoreUpdate({
                      contactInfo: {
                        email:
                          storeData.contactInfo?.email || "info@kentestore.com",
                        phone:
                          storeData.contactInfo?.phone || "+233 24 123 4567",
                        location: e.target.value,
                        businessHours: storeData.contactInfo?.businessHours || {
                          weekdays: "9:00 AM - 6:00 PM",
                          saturday: "10:00 AM - 4:00 PM",
                          sunday: "Closed",
                        },
                      },
                    })
                  }
                  placeholder="Accra, Ghana"
                />
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">Business Hours</Label>

                <div className="space-y-2">
                  <Label htmlFor="hours-weekdays" className="text-xs">
                    Monday - Friday
                  </Label>
                  <Input
                    id="hours-weekdays"
                    value={
                      storeData.contactInfo?.businessHours?.weekdays ||
                      "9:00 AM - 6:00 PM"
                    }
                    onChange={(e) =>
                      onStoreUpdate({
                        contactInfo: {
                          email:
                            storeData.contactInfo?.email ||
                            "info@kentestore.com",
                          phone:
                            storeData.contactInfo?.phone || "+233 24 123 4567",
                          location:
                            storeData.contactInfo?.location || "Accra, Ghana",
                          businessHours: {
                            weekdays: e.target.value,
                            saturday:
                              storeData.contactInfo?.businessHours?.saturday ||
                              "10:00 AM - 4:00 PM",
                            sunday:
                              storeData.contactInfo?.businessHours?.sunday ||
                              "Closed",
                          },
                        },
                      })
                    }
                    placeholder="9:00 AM - 6:00 PM"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hours-saturday" className="text-xs">
                    Saturday
                  </Label>
                  <Input
                    id="hours-saturday"
                    value={
                      storeData.contactInfo?.businessHours?.saturday ||
                      "10:00 AM - 4:00 PM"
                    }
                    onChange={(e) =>
                      onStoreUpdate({
                        contactInfo: {
                          email:
                            storeData.contactInfo?.email ||
                            "info@kentestore.com",
                          phone:
                            storeData.contactInfo?.phone || "+233 24 123 4567",
                          location:
                            storeData.contactInfo?.location || "Accra, Ghana",
                          businessHours: {
                            weekdays:
                              storeData.contactInfo?.businessHours?.weekdays ||
                              "9:00 AM - 6:00 PM",
                            saturday: e.target.value,
                            sunday:
                              storeData.contactInfo?.businessHours?.sunday ||
                              "Closed",
                          },
                        },
                      })
                    }
                    placeholder="10:00 AM - 4:00 PM"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hours-sunday" className="text-xs">
                    Sunday
                  </Label>
                  <Input
                    id="hours-sunday"
                    value={
                      storeData.contactInfo?.businessHours?.sunday || "Closed"
                    }
                    onChange={(e) =>
                      onStoreUpdate({
                        contactInfo: {
                          email:
                            storeData.contactInfo?.email ||
                            "info@kentestore.com",
                          phone:
                            storeData.contactInfo?.phone || "+233 24 123 4567",
                          location:
                            storeData.contactInfo?.location || "Accra, Ghana",
                          businessHours: {
                            weekdays:
                              storeData.contactInfo?.businessHours?.weekdays ||
                              "9:00 AM - 6:00 PM",
                            saturday:
                              storeData.contactInfo?.businessHours?.saturday ||
                              "10:00 AM - 4:00 PM",
                            sunday: e.target.value,
                          },
                        },
                      })
                    }
                    placeholder="Closed"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview Panel */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Live Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className="border rounded-lg overflow-hidden"
                style={{
                  backgroundColor: storeData.primaryColor,
                  color: "white",
                }}
              >
                {/* Mock Store Header */}
                <div
                  className="p-4 border-b"
                  style={{ backgroundColor: storeData.secondaryColor }}
                >
                  <div className="flex items-center gap-3">
                    {storeData.logo && (
                      <img
                        src={storeData.logo}
                        alt="Logo"
                        className="h-8 w-8 object-contain"
                      />
                    )}
                    <h1 className="text-lg font-bold">
                      {storeData.name || "My Kente Store"}
                    </h1>
                  </div>
                </div>

                {/* Mock Navigation */}
                <div
                  className="flex gap-6 p-4 shadow-sm"
                  style={{ backgroundColor: storeData.secondaryColor }}
                >
                  <a
                    href="#"
                    className="text-white hover:underline font-medium transition-all duration-200 hover:scale-105"
                  >
                    Home
                  </a>
                  <a
                    href="#"
                    className="text-white hover:underline transition-all duration-200 hover:scale-105"
                  >
                    About
                  </a>
                  <a
                    href="#"
                    className="text-white hover:underline transition-all duration-200 hover:scale-105"
                  >
                    Store
                  </a>
                  <a
                    href="#"
                    className="text-white hover:underline transition-all duration-200 hover:scale-105"
                  >
                    Contact
                  </a>
                </div>

                {/* Mock Hero Section */}
                <div
                  className="p-6 text-center"
                  style={{
                    backgroundColor: storeData.accentColor,
                    color: storeData.primaryColor,
                  }}
                >
                  <h2 className="text-2xl font-bold mb-4">
                    Welcome to {storeData.name || "My Kente Store"}
                  </h2>
                  <p className="text-sm opacity-80 mb-4">
                    {storeData.description ||
                      "Authentic Kente designs crafted with love"}
                  </p>
                  <div className="flex gap-2 justify-center">
                    <button className="px-4 py-2 bg-white text-gray-900 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-all duration-200 hover:scale-105 shadow-md">
                      Shop Now
                    </button>
                    <button className="px-4 py-2 border-2 border-white bg-white/10 text-white rounded-lg text-sm font-semibold hover:bg-white hover:text-gray-900 transition-all duration-200 hover:scale-105 backdrop-blur-sm">
                      Learn More
                    </button>
                  </div>
                </div>

                {/* Mock Store Items */}
                <div
                  className="p-4"
                  style={{
                    backgroundColor: "white",
                    color: storeData.primaryColor,
                  }}
                >
                  <h3 className="text-lg font-semibold mb-4">
                    Featured Products
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {[1, 2].map((i) => (
                      <div
                        key={i}
                        className="border rounded-lg p-3 hover:shadow-lg transition-all duration-200 hover:-translate-y-1"
                      >
                        <div className="aspect-square bg-gray-100 rounded mb-2 relative">
                          <div className="absolute top-2 right-2">
                            <button className="h-6 w-6 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all duration-200 hover:scale-110">
                              <span className="text-xs">♡</span>
                            </button>
                          </div>
                        </div>
                        <h4 className="font-medium text-sm">Kente Item {i}</h4>
                        <p className="text-xs text-gray-600 mb-2">
                          Beautiful traditional design
                        </p>
                        <div className="flex items-center justify-between">
                          <p
                            className="font-bold text-lg"
                            style={{ color: storeData.accentColor }}
                          >
                            ₵{50 + i * 25}
                          </p>
                          <button
                            className="px-3 py-1 text-xs font-semibold rounded transition-all duration-200 hover:scale-105 shadow-sm"
                            style={{
                              backgroundColor: storeData.primaryColor,
                              color: "white",
                            }}
                          >
                            Add to Cart
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Mock Footer */}
                <div
                  className="p-4 border-t"
                  style={{
                    backgroundColor: storeData.primaryColor,
                    color: "white",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {storeData.logo && (
                        <img
                          src={storeData.logo}
                          alt="Logo"
                          className="h-6 w-6 object-contain"
                        />
                      )}
                      <span className="text-sm font-semibold">
                        {storeData.name || "My Kente Store"}
                      </span>
                    </div>
                    <div className="flex gap-4 text-xs">
                      <button className="hover:underline transition-opacity">
                        Privacy
                      </button>
                      <button className="hover:underline transition-opacity">
                        Terms
                      </button>
                      <span className="opacity-70">© 2025</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Color Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Color Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-6 h-6 rounded border"
                    style={{ backgroundColor: storeData.primaryColor }}
                  />
                  <span className="text-sm">
                    Primary: {storeData.primaryColor}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div
                    className="w-6 h-6 rounded border"
                    style={{ backgroundColor: storeData.secondaryColor }}
                  />
                  <span className="text-sm">
                    Secondary: {storeData.secondaryColor}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div
                    className="w-6 h-6 rounded border"
                    style={{ backgroundColor: storeData.accentColor }}
                  />
                  <span className="text-sm">
                    Accent: {storeData.accentColor}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default WebsiteCustomizer;
